/**
 * Canal temps reel du widget.
 *
 * Il sert a UNE chose : recevoir ce qu'un conseiller humain ecrit pendant une
 * prise en main, et savoir quand elle commence et s'arrete. Le widget continue
 * d'ECRIRE par HTTP — le canal est descendant.
 *
 * ## Ce qu'il ne garantit pas, et pourquoi c'est assume
 *
 * Rien n'est rejoue a la reconnexion : le serveur ne tamponne aucun evenement,
 * parce qu'un tampon en memoire donnerait une illusion de garantie qu'un simple
 * redemarrage de l'API ne respecterait pas. Un message manque se retrouve au
 * rechargement de la page, par l'historique que rend `/runtime/session`.
 *
 * ## Degradation
 *
 * Tout echec est silencieux. Un navigateur sans WebSocket, un proxy d'entreprise
 * qui bloque l'upgrade, une coupure : le widget retombe sur son fonctionnement
 * normal, ou seul l'envoi d'un message rapporte des nouvelles. Une bulle de chat
 * qui affiche « connexion temps reel impossible » n'aide aucun visiteur.
 */

import { API_BASE } from './api';

export type EvenementRecu =
  | {
      type: 'message';
      message: { id: string; role: 'user' | 'assistant' | 'admin'; content: string; auteur?: string };
    }
  | { type: 'statut'; statut: 'ai_active' | 'handover_requested' | 'human_active' | 'closed'; operateur?: string };

type Options = {
  publicId: string;
  sessionId: string;
  onEvenement: (evenement: EvenementRecu) => void;
};

/** Battement de coeur : nginx ferme une socket inactive au bout de 300 s. */
const BATTEMENT_MS = 45_000;

/**
 * Attentes de reconnexion, en millisecondes.
 *
 * Croissantes puis plafonnees : un visiteur dont la connexion tombe ne doit pas
 * marteler le serveur, mais une coupure de deux secondes ne doit pas lui couter
 * une minute d'attente. Au-dela du dernier palier, on reste a une minute.
 */
const ATTENTES_MS = [1_000, 3_000, 10_000, 30_000, 60_000];

export type Canal = { fermer: () => void };

export function ouvrirCanal(options: Options): Canal {
  let socket: WebSocket | null = null;
  let battement: ReturnType<typeof setInterval> | undefined;
  let reprise: ReturnType<typeof setTimeout> | undefined;
  let echecs = 0;
  let ferme = false;

  const urlCanal = (): string => {
    const base = API_BASE.replace(/^http/, 'ws');
    return `${base}/temps-reel?conversation=${encodeURIComponent(
      options.sessionId
    )}&site=${encodeURIComponent(options.publicId)}`;
  };

  const planifierReprise = (): void => {
    if (ferme) return;
    const attente = ATTENTES_MS[Math.min(echecs, ATTENTES_MS.length - 1)] ?? 60_000;
    echecs += 1;
    clearTimeout(reprise);
    reprise = setTimeout(connecter, attente);
  };

  function connecter(): void {
    if (ferme) return;
    if (typeof WebSocket === 'undefined') return;

    try {
      socket = new WebSocket(urlCanal());
    } catch {
      planifierReprise();
      return;
    }

    socket.onopen = () => {
      // Remis a zero seulement ICI : compter l'ouverture et non la tentative
      // evite qu'une boucle « ouvre puis ferme aussitot » reste a une seconde
      // d'attente indefiniment.
      echecs = 0;
      clearInterval(battement);
      battement = setInterval(() => {
        if (socket?.readyState === WebSocket.OPEN) socket.send('ping');
      }, BATTEMENT_MS);
    };

    socket.onmessage = (evenement) => {
      try {
        const charge = JSON.parse(String(evenement.data)) as EvenementRecu | { type: string };
        if (charge.type === 'message' || charge.type === 'statut') {
          options.onEvenement(charge as EvenementRecu);
        }
      } catch {
        // Charge illisible : on ignore. Le fil reste correct au rechargement.
      }
    };

    socket.onclose = () => {
      clearInterval(battement);
      planifierReprise();
    };

    socket.onerror = () => {
      // `onclose` suit toujours `onerror` : planifier ici aussi produirait deux
      // reconnexions concurrentes pour une seule coupure.
      socket?.close();
    };
  }

  connecter();

  return {
    fermer: () => {
      ferme = true;
      clearInterval(battement);
      clearTimeout(reprise);
      socket?.close();
    },
  };
}
